import { BasketSummarySubjectType } from '@api/api/basket-summary';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';

import { Drawer } from '@design/components/drawer/Drawer';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useScrollListener } from '@helpers/hooks/use-scroll-listener/useScrollListener';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { NoDataFound } from '@terminal/components/no-data-found/NoDataFound';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useKgInsightsByIdResult } from '@terminal/react-query-hooks/insights/useKgInsight';
import {
  CATEGORY_ORDER,
  usePulseSentimentInsights,
} from '@terminal/react-query-hooks/themes/usePulseSentimentInsights';
import { queryKeys } from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { useCatalystDrawer } from '@terminal/widgets/catalyst-insights-drawer/useCatalystDrawer';
import { useEarningsInsightDrawer } from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import { useMarketCatalystDrawer } from '@terminal/widgets/market-catalyst-drawer/useMarketCatalystDrawer';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePulseMonthlySummaries } from '../hooks/usePulseSummaries';
import {
  PulseInsightCategory,
  PulseSentimentDirection,
  PulseSentimentInsight,
} from '../pulse-types';
import { citationIdsForDirection } from '../utils/citationIdsForDirection';
import { openDrawerForInsight } from '../utils/openDrawerForInsight';
import { toPulseInsight } from '../utils/toPulseInsight';
import styles from './MonthlySentimentDrawer.module.scss';
import { CATEGORY_CLASS, PulseInsightCard } from './PulseInsightCard';
import pillStyles from './PulseInsightCard.module.scss';

const isPulseDirection = (
  value: string | null
): value is PulseSentimentDirection =>
  value === 'tailwind' || value === 'headwind' || value === 'all';

const DrawerContent = ({
  id,
  subjectType = 'theme',
  direction,
  showAllDrivers,
  setShowAllDrivers,
  onOpenInsight,
}: {
  id?: string;
  subjectType?: BasketSummarySubjectType;
  direction: PulseSentimentDirection;
  showAllDrivers: boolean;
  setShowAllDrivers: (showAllDrivers: boolean) => void;
  onOpenInsight: (insight: PulseSentimentInsight) => void;
}) => {
  const { t } = useTranslation(['widget']);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] =
    useState<PulseInsightCategory | null>(null);

  const monthlySummaries = usePulseMonthlySummaries({
    id: id ?? '',
    subjectType,
  });
  // TODO: Handle scenario insights in citations / pinned insights
  const { data: citedInsightsById, isLoading: areCitedInsightsLoading } =
    useKgInsightsByIdResult(
      citationIdsForDirection(monthlySummaries.data, direction)
    );
  const pinnedInsights = [...citedInsightsById.values()].map(insight =>
    toPulseInsight(insight, direction)
  );

  const {
    data: allDriverInsights,
    counts: allDriverCounts,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePulseSentimentInsights({
    id: id ?? '',
    subjectType,
    direction,
    category: activeCategory,
    enabled: !!id && !monthlySummaries.isLoading,
    pinnedInsights,
  });

  useScrollListener(
    cardsRef,
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage, isLoading],
    { isGlobalScrollListened: false, offset: 100 }
  );

  const isInitialLoad =
    monthlySummaries.isLoading ||
    (!showAllDrivers && areCitedInsightsLoading) ||
    (showAllDrivers && isLoading && !activeCategory);

  if (isInitialLoad) {
    return (
      <SkeletonLoader
        className={styles.skeletonLoader}
        areas={[
          { styles: { width: '100%', height: '32px', borderRadius: '8px' } },
          { styles: { width: '100%', height: '160px', borderRadius: '8px' } },
          { styles: { width: '100%', height: '160px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (showAllDrivers && isError) {
    return <ErrorMessageWrapper displayIcon variant="error" />;
  }

  const data = showAllDrivers
    ? allDriverInsights
    : pinnedInsights.filter(
        insight => !activeCategory || insight.category === activeCategory
      );
  const allDriversEmpty = CATEGORY_ORDER.every(
    category => allDriverCounts[category].count === 0
  );
  const counts = showAllDrivers
    ? allDriverCounts
    : CATEGORY_ORDER.reduce(
        (result, category) => {
          result[category] = {
            count: pinnedInsights.filter(
              insight => insight.category === category
            ).length,
            hasMore: false,
          };

          return result;
        },
        {} as typeof allDriverCounts
      );
  const isListLoading =
    isLoading && (showAllDrivers || (data.length === 0 && allDriversEmpty));
  const visibleCategories = CATEGORY_ORDER.filter(
    category => category === activeCategory || counts[category].count > 0
  );
  const noKeyDriversTitle =
    /* i18next-extract-mark-context-next-line ["tailwind", "headwind"] */
    t('widget:themeOverview.pulse.drawer.noKeyDrivers', {
      context: direction,
    });
  const canViewAllDrivers = !showAllDrivers && !allDriversEmpty;

  return (
    <div className={styles.content}>
      {visibleCategories.length > 0 && (
        <div className={styles.chips}>
          {visibleCategories.map(category => {
            const { count, hasMore } = counts[category];
            const countLabel = hasMore ? `${count}+` : count;

            return (
              <button
                key={category}
                type="button"
                className={clsx(
                  pillStyles.categoryPill,
                  CATEGORY_CLASS[category],
                  styles.filterChip,
                  { [styles.filterChipActive]: activeCategory === category }
                )}
                aria-pressed={activeCategory === category}
                onClick={() =>
                  setActiveCategory(current =>
                    current === category ? null : category
                  )
                }
              >
                {t(
                  `widget:themeOverview.pulse.drawer.categoryFilter_${category}`,
                  { count: countLabel }
                )}
              </button>
            );
          })}
        </div>
      )}
      <div ref={cardsRef} className={styles.cards}>
        {isListLoading ? (
          <SkeletonLoader
            className={styles.skeletonLoader}
            areas={[
              {
                styles: { width: '100%', height: '160px', borderRadius: '8px' },
              },
              {
                styles: { width: '100%', height: '160px', borderRadius: '8px' },
              },
            ]}
          />
        ) : (
          <>
            {data.map(insight => (
              <PulseInsightCard
                key={insight.id}
                insight={insight}
                onOpen={() => onOpenInsight(insight)}
              />
            ))}
            {data.length === 0 && (
              <NoDataFound
                bannerPath="/app/static/images/empty-state/radio-telescope.png"
                className={styles.empty}
                title={noKeyDriversTitle}
                description={
                  canViewAllDrivers
                    ? t(
                        'widget:themeOverview.pulse.drawer.noKeyDriversDescription'
                      )
                    : undefined
                }
                buttonAction={
                  canViewAllDrivers ? () => setShowAllDrivers(true) : undefined
                }
                buttonLabel={
                  canViewAllDrivers
                    ? t('widget:themeOverview.pulse.drawer.viewAllDrivers')
                    : undefined
                }
                buttonSize="small"
              />
            )}
          </>
        )}
        {showAllDrivers && hasNextPage && (
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: '100px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};

export const MonthlySentimentDrawer = ({
  themeId,
  entityTag,
  subjectType,
  source = POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
}: {
  themeId?: string;
  entityTag?: string;
  subjectType?: BasketSummarySubjectType;
  source?: PosthogTrackingSource;
}) => {
  const { t } = useTranslation(['widget']);
  const { params, deleteQueryParam } = useQueryParams();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const { openDrawer: openCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openEarningsInsightDrawer } = useEarningsInsightDrawer();
  const { openDrawer: openScenarioInsightDrawer } = useInsightDrawer();
  const isAssetOverview = source === POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW;
  const initialShowAllDrivers = isAssetOverview;
  const [showAllDrivers, setShowAllDrivers] = useState<boolean>(
    initialShowAllDrivers
  );

  const direction = params[queryKeys.monthlySentiment];
  const openDirection = isPulseDirection(direction) ? direction : null;

  const closeDrawer = () => {
    setShowAllDrivers(initialShowAllDrivers);
    deleteQueryParam(queryKeys.monthlySentiment, { replace: true });
  };

  const openInsight = (insight: PulseSentimentInsight) => {
    openDrawerForInsight({
      insight,
      source,
      fallbackThemeId: themeId,
      openMarketCatalystDrawer,
      openCatalystDrawer,
      openEarningsInsightDrawer,
      openScenarioInsightDrawer,
    });
  };

  return (
    <Drawer
      isOpen={openDirection !== null}
      handleCloseClick={closeDrawer}
      data-testid="pulse-monthly-sentiment-drawer"
    >
      {openDirection && (
        <DrawerContentWrapper className={styles.wrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>
              {isAssetOverview
                ? t(
                    `widget:assetOverview.sentiment.monthlyDirectionSentiment_${direction}`
                  )
                : t(`widget:themeOverview.pulse.drawer.title_${openDirection}`)}
            </h2>
            {!isAssetOverview && (
              <ButtonGroup
                className={styles.driverScope}
                labels={[
                  t('widget:themeOverview.pulse.drawer.keyDrivers'),
                  t('widget:themeOverview.pulse.drawer.allDrivers'),
                ]}
                activeIndex={showAllDrivers ? 1 : 0}
                onClick={index => setShowAllDrivers(index === 1)}
                aria-label={t('widget:themeOverview.pulse.drawer.driverScope')}
              />
            )}
          </div>
          <DrawerContent
            id={themeId ?? entityTag}
            subjectType={subjectType}
            direction={openDirection}
            showAllDrivers={showAllDrivers}
            setShowAllDrivers={setShowAllDrivers}
            onOpenInsight={openInsight}
          />
        </DrawerContentWrapper>
      )}
    </Drawer>
  );
};
